import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import styled from 'styled-components/macro';

import colors from 'Application/theme/colors';

import Link from 'Common/components/Link';
import Icon from 'Common/components/icons/Icon';
import Button from 'Common/components/buttons/Button';
import FormField from 'Common/components/form/FormField';
import FormikInput from 'Common/components/form/FormikInput';
import useQueryParameter from 'Common/hooks/useQueryParameter';
import showAxiosResponseError from 'Common/utils/showAxiosResponseError';

import useConfirmAccountFormInitialValues from 'User/components/ConfirmAccount/ConfirmAccountForm/hooks/useConfirmAccountFormInitialValues';
import useConfirmAccountFormValidationSchema from 'User/components/ConfirmAccount/ConfirmAccountForm/hooks/useConfirmAccountFormValidationSchema';

import login from 'User/services/login';
import confirmAccount from 'User/api/confirmAccount';
import useAbortController from 'Common/hooks/useAbortController';

const ButtonWrapper = styled.div`
	display: flex;
	height: 100px;
	justify-content: flex-end;
	align-items: flex-end;
`;

const StyledFormField = styled(FormField)`
	position: relative;
	margin-bottom: 32px;
`;

const IconContainer = styled.div`
	position: absolute;
	right: 5px;
	bottom: 5px;
`;

const ConfirmAccountForm = () => {
	const { t } = useTranslation();
	const [showPassword, setShowPassword] = useState(false);
	const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);

	const abortController = useAbortController();

	const email = useQueryParameter('email');
	const code = useQueryParameter('code');

	const initialValues = useConfirmAccountFormInitialValues({});
	const validationSchema = useConfirmAccountFormValidationSchema();

	const { errors, touched, values, handleSubmit, handleChange, handleBlur, setFieldError } = useFormik({
		initialValues,
		validationSchema,
		onSubmit: async values => {
			try {
				await confirmAccount(abortController.signal, email, values.password, values.passwordConfirm, code);

				await login(abortController.signal, email, values.password, true);
			} catch (error) {
				showAxiosResponseError({
					error,
					setFormikFieldError: setFieldError,
					toastFields: ['username', 'code'],
					fieldsMapping: {
						username: 'E-mail',
						code: 'Code',
					},
				});
			}
		},
	});

	return (
		<form onSubmit={handleSubmit}>
			<StyledFormField>
				<FormikInput
					id="password"
					name="password"
					inputType={showPassword ? 'text' : 'password'}
					label={t('Password')}
					value={values.password}
					error={errors.password}
					touched={touched.password}
					onChange={handleChange}
					onBlur={handleBlur}
					isRequired
					placeholder="Password"
				/>
				<IconContainer>
					<Icon
						icon="removeRedEyeOutlined"
						color={showPassword ? colors.primary.main : ''}
						onClick={() => setShowPassword(prev => !prev)}
					/>
				</IconContainer>
			</StyledFormField>
			<StyledFormField>
				<FormikInput
					id="passwordConfirm"
					name="passwordConfirm"
					inputType={showPasswordRepeat ? 'text' : 'password'}
					label={t('Confirm Password')}
					value={values.passwordConfirm}
					error={errors.passwordConfirm}
					touched={touched.passwordConfirm}
					onChange={handleChange}
					onBlur={handleBlur}
					isRequired
					placeholder="Confirm Password"
				/>
				<IconContainer>
					<Icon
						icon="removeRedEyeOutlined"
						color={showPasswordRepeat ? colors.primary.main : ''}
						onClick={() => setShowPasswordRepeat(prev => !prev)}
					/>
				</IconContainer>
			</StyledFormField>
			<Link to="/login">go back to login page</Link>
			<ButtonWrapper>
				<Button type="submit" text={t('Save Password')} label="Confirm Account Form - Save Password Button" />
			</ButtonWrapper>
		</form>
	);
};

export default ConfirmAccountForm;
